/**
 * API Utility Functions
 * Purpose: Centralized API communication with backend services
 * Author: Traklet App
 * Created: 2025
 */

import axios, { AxiosError } from 'axios';
import TokenStorage from './tokenStorage';

// Environment-based API Configuration
const getApiBaseUrl = () => {
  // Check if we're in production (digivyn.com domain)
  const isProduction = window.location.hostname.includes('digivyn.com');
  
  if (isProduction) {
    // New clean structure: API endpoints under /api
    return `${window.location.protocol}//${window.location.host}/api`;
  }
  
  // Development environment - use /api prefix for Vite proxy
  return '/api';
};

const API_BASE_URL = getApiBaseUrl();

// Log current environment configuration
console.log(`[API Config] Environment: ${window.location.hostname.includes('digivyn.com') ? 'production' : 'development'}`);
console.log(`[API Config] Base URL: ${API_BASE_URL}`);
console.log(`[API Config] Domain: ${window.location.hostname}`);

// Create axios instance with default configuration
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
});

// Request interceptor for logging and JWT token injection
apiClient.interceptors.request.use(
  (config) => {
    console.log(`[API Request] ${config.method?.toUpperCase()} ${config.url}`);
    
    // Add JWT token to headers if available
    const authHeaders = TokenStorage.getAuthHeader();
    if ('Authorization' in authHeaders && authHeaders.Authorization) {
      config.headers.Authorization = authHeaders.Authorization;
      console.log('[API Request] JWT token added to request');
    }
    
    return config;
  },
  (error) => {
    console.error('[API Request Error]', error);
    return Promise.reject(error);
  }
);

// Response interceptor for logging and error handling
apiClient.interceptors.response.use(
  (response) => {
    console.log(`[API Response] ${response.status} ${response.config.url}`);
    return response;
  },
  (error: AxiosError) => {
    console.error('[API Response Error]', error.response?.status, error.response?.data);
    
    // Handle authentication errors
    if (error.response?.status === 401) {
      console.log('[API] 401 error detected - clearing invalid token');
      TokenStorage.clearAll();
      // Dispatch custom event for auth state change
      window.dispatchEvent(new CustomEvent('auth-token-expired'));
    }
    
    return Promise.reject(error);
  }
);

// Type definitions
export interface ApiResponse<T = any> {
  success: boolean;
  message: string;
  data?: T;
  errors?: string[];
}

export interface User {
  id: number;
  email: string;
  username: string;
  role: string;
}

export interface UrlData {
  id: number;
  short_code: string;
  short_url: string;
  long_url: string;
  original_url: string;
  click_count: number;
  created_at: string;
  last_clicked?: string;
  user_id: number;
  created_by: string;
  user_email?: string; // For admin view
}

export interface ClickData {
  id: number;
  clicked_at: string;
  ip_address: string;
  user_agent: string;
  referrer?: string;
}

export interface UrlAnalytics {
  url: {
    id: number;
    short_code: string;
    short_url: string;
    long_url: string;
  };
  statistics: {
    total_clicks: number;
    daily_clicks: Array<{ date: string; clicks: number }>;
    top_referrers: Array<{ referrer: string; clicks: number }>;
    browser_stats: Array<{ browser: string; clicks: number }>;
  };
  click_details?: ClickData[];
  pagination?: {
    limit: number;
    offset: number;
    has_more: boolean;
  };
}

export interface PaginatedUrls {
  urls: UrlData[];
  pagination: {
    total_count: number;
    current_page: number;
    total_pages: number;
    limit: number;
    offset: number;
    has_more: boolean;
  };
}

// Authentication API
export const authApi = {
  /**
   * Register new user
   */
  register: async (username: string, email: string, password: string, confirmPassword?: string): Promise<ApiResponse<{ token: string; user: User; expires_in: number; expires_at: string }>> => {
    const payload = { 
      username, 
      email, 
      password,
      confirm_password: confirmPassword || password
    };
    const response = await apiClient.post('/register.php', payload);
    return response.data;
  },

  /**
   * Login user with JWT
   */
  login: async (email: string, password: string): Promise<ApiResponse<{ token: string; user: User }>> => {
    const response = await apiClient.post('/login.php', { email, password });
    return response.data;
  },

  /**
   * Logout user (clear local token)
   */
  logout: async (): Promise<ApiResponse> => {
    // For JWT, logout is handled client-side by clearing the token
    TokenStorage.clearAll();
    return Promise.resolve({
      success: true,
      message: 'Logout successful'
    });
  },

  /**
   * Check authentication status with JWT
   */
  checkAuth: async (): Promise<ApiResponse<{ user: User; authenticated: boolean }>> => {
    // First check if we have a token locally
    if (!TokenStorage.getToken() || TokenStorage.isTokenExpired()) {
      return Promise.resolve({
        success: false,
        authenticated: false,
        message: 'No valid token found'
      });
    }

    // Validate token with server
    const response = await apiClient.get('/login.php');
    return response.data;
  },
};

// URL Management API
export const urlApi = {
  /**
   * Shorten a URL
   */
  shorten: async (longUrl: string, customAlias?: string): Promise<ApiResponse<UrlData>> => {
    const payload: any = { long_url: longUrl };
    if (customAlias) {
      payload.custom_alias = customAlias;
    }
    const response = await apiClient.post('/shorten.php', payload);
    return response.data;
  },

  /**
   * Get user's URLs with pagination and search
   */
  getUrls: async (params?: {
    limit?: number;
    offset?: number;
    search?: string;
    sort_by?: string;
    sort_order?: string;
  }): Promise<ApiResponse<PaginatedUrls>> => {
    const response = await apiClient.get('/get_links.php', { params });
    return response.data;
  },

  /**
   * Delete a URL
   */
  deleteUrl: async (urlId: number): Promise<ApiResponse> => {
    const response = await apiClient.post('/delete_link.php', { id: urlId });
    return response.data;
  },

  /**
   * Bulk delete URLs
   */
  bulkDeleteUrls: async (urlIds: number[]): Promise<ApiResponse> => {
    const response = await apiClient.post('/delete_link.php?action=bulk', { ids: urlIds });
    return response.data;
  },
};

// Analytics API
export const analyticsApi = {
  /**
   * Get click analytics for a URL
   */
  getClickAnalytics: async (urlId: number, params?: {
    limit?: number;
    offset?: number;
    date_from?: string;
    date_to?: string;
    include_details?: boolean;
  }): Promise<ApiResponse<UrlAnalytics>> => {
    const queryParams = { url_id: urlId, ...params };
    const response = await apiClient.get('/get_clicks.php', { params: queryParams });
    return response.data;
  },

  /**
   * Get enhanced analytics for a URL
   */
  getEnhancedAnalytics: async (urlId: number, type: string, params?: {
    date_from?: string;
    date_to?: string;
    exclude_bots?: boolean;
  }): Promise<ApiResponse<any>> => {
    const queryParams = { 
      url_id: urlId, 
      type,
      ...params 
    };
    const response = await apiClient.get('/enhanced_analytics_api.php', { params: queryParams });
    return response.data;
  },
};

// Admin API
export const adminApi = {
  /**
   * Get dashboard statistics
   */
  getDashboardStats: async (): Promise<ApiResponse<any>> => {
    const response = await apiClient.get('/admin.php?action=dashboard');
    return response.data;
  },

  /**
   * Get users with pagination and filters
   */
  getUsers: async (params?: {
    limit?: number;
    offset?: number;
    search?: string;
    role?: string;
    status?: string;
  }): Promise<ApiResponse<any>> => {
    const queryParams = { action: 'users', ...params };
    const response = await apiClient.get('/admin.php', { params: queryParams });
    return response.data;
  },

  /**
   * Get recent registrations
   */
  getRecentRegistrations: async (hours: number = 24): Promise<ApiResponse<any>> => {
    const response = await apiClient.get('/admin.php', {
      params: { action: 'recent_registrations', hours }
    });
    return response.data;
  },

  /**
   * Update user status (activate/deactivate)
   */
  updateUserStatus: async (userId: number, isActive: boolean): Promise<ApiResponse> => {
    const response = await apiClient.post('/admin.php', {
      action: 'update_user_status',
      user_id: userId,
      is_active: isActive
    });
    return response.data;
  },

  /**
   * Delete user and associated data
   */
  deleteUser: async (userId: number): Promise<ApiResponse> => {
    const response = await apiClient.post('/admin.php', {
      action: 'delete_user',
      user_id: userId
    });
    return response.data;
  },
};

// Utility functions
export const apiUtils = {
  /**
   * Handle API errors and extract error message
   */
  getErrorMessage: (error: any): string => {
    if (error.response?.data?.message) {
      return error.response.data.message;
    }
    if (error.response?.data?.errors?.length > 0) {
      return error.response.data.errors.join(', ');
    }
    if (error.message) {
      return error.message;
    }
    return 'An unexpected error occurred';
  },

  /**
   * Check if error is a network error
   */
  isNetworkError: (error: any): boolean => {
    return !error.response && error.request;
  },

  /**
   * Check if error is a server error (5xx)
   */
  isServerError: (error: any): boolean => {
    return error.response?.status >= 500;
  },

  /**
   * Check if error is a client error (4xx)
   */
  isClientError: (error: any): boolean => {
    return error.response?.status >= 400 && error.response?.status < 500;
  },
};

export default apiClient;